import { Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/client/components/confirm-dialog";
import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Skeleton } from "@/client/components/ui/skeleton";
import {
  type ChannelSendResult,
  type CreateWindowInput,
  type MaintenanceChannel,
  type StaffMaintenanceWindow,
  maintenanceAdminApi,
} from "@/client/features/admin/maintenance-admin-api";
import { type AdminTenant, tenantsAdminApi } from "@/client/features/admin/tenants-admin-api";
import type { MaintenanceStatus } from "@/client/features/maintenance/maintenance-api";
import { ApiError } from "@/client/lib/api";

const textareaClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";
const selectClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

const STATUS_LABELS: Record<MaintenanceStatus, string> = {
  scheduled: "Scheduled",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

/** ISO string -> value for <input type="datetime-local"> in local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  return new Date(value).toISOString();
}

interface FormState {
  title: string;
  description: string;
  kind: "scheduled" | "emergency";
  audience: "all" | "selected";
  startsAt: string;
  endsAt: string;
  emailOnPublish: boolean;
  publish: boolean;
  tenantIds: string[];
}

function emptyForm(): FormState {
  return {
    title: "",
    description: "",
    kind: "scheduled",
    audience: "selected",
    startsAt: "",
    endsAt: "",
    emailOnPublish: false,
    publish: false,
    tenantIds: [],
  };
}

function formFromWindow(w: StaffMaintenanceWindow): FormState {
  return {
    title: w.title,
    description: w.description,
    kind: w.kind,
    audience: w.audience,
    startsAt: toLocalInput(w.startsAt),
    endsAt: toLocalInput(w.endsAt),
    emailOnPublish: w.emailOnPublish,
    publish: Boolean(w.publishedAt),
    tenantIds: w.tenantTargets.map((t) => t.id),
  };
}

function WindowFormDialog({
  trigger,
  title,
  tenants,
  initial,
  onSubmit,
}: {
  trigger: React.ReactNode;
  title: string;
  tenants: AdminTenant[];
  initial: FormState;
  onSubmit: (input: CreateWindowInput) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(initial);
  const [saving, setSaving] = useState(false);

  // `initial` is a fresh object on every parent render (formFromWindow(window)),
  // so it must not be an effect dependency or an unrelated re-render (e.g. another
  // card's Slack send refreshing the list) would wipe in-progress edits. Seed the
  // form only on the open transition, reading the latest initial via a ref.
  const initialRef = useRef(initial);
  initialRef.current = initial;
  useEffect(() => {
    if (open) setForm(initialRef.current);
  }, [open]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const toggleTenant = (id: string) =>
    setForm((f) => ({
      ...f,
      tenantIds: f.tenantIds.includes(id)
        ? f.tenantIds.filter((t) => t !== id)
        : [...f.tenantIds, id],
    }));

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (!form.startsAt) {
      toast.error("Start time is required.");
      return;
    }
    if (form.endsAt && new Date(form.endsAt) <= new Date(form.startsAt)) {
      toast.error("End time must be after the start time.");
      return;
    }
    if (form.audience === "selected" && form.tenantIds.length === 0) {
      toast.error("Select at least one tenant, or choose All tenants.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        title: form.title.trim(),
        description: form.description,
        kind: form.kind,
        audience: form.audience,
        startsAt: fromLocalInput(form.startsAt),
        endsAt: form.endsAt ? fromLocalInput(form.endsAt) : null,
        emailOnPublish: form.emailOnPublish,
        tenantIds: form.audience === "selected" ? form.tenantIds : [],
        publish: form.publish,
      });
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the window.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Banner and calendar update as soon as the window is published. Slack and email are sent
            separately from the window's actions.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="mw-title">Title</Label>
            <Input
              id="mw-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Network upgrade in Rack B"
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="mw-desc">Description</Label>
            <textarea
              id="mw-desc"
              className={textareaClass}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="What's happening and the expected impact."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="mw-kind">Type</Label>
              <select
                id="mw-kind"
                className={selectClass}
                value={form.kind}
                onChange={(e) => update("kind", e.target.value as FormState["kind"])}
              >
                <option value="scheduled">Scheduled</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mw-audience">Audience</Label>
              <select
                id="mw-audience"
                className={selectClass}
                value={form.audience}
                onChange={(e) => update("audience", e.target.value as FormState["audience"])}
              >
                <option value="selected">Selected tenants</option>
                <option value="all">All tenants</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="mw-start">Starts</Label>
              <Input
                id="mw-start"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => update("startsAt", e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mw-end">Ends (optional)</Label>
              <Input
                id="mw-end"
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => update("endsAt", e.target.value)}
              />
            </div>
          </div>

          {form.audience === "selected" ? (
            <div className="grid gap-1.5">
              <Label>Tenants</Label>
              <div className="max-h-40 overflow-y-auto rounded-md border p-2">
                {tenants.length === 0 ? (
                  <p className="text-muted-foreground text-xs">No tenants available.</p>
                ) : (
                  tenants.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 py-1 text-sm">
                      <input
                        type="checkbox"
                        checked={form.tenantIds.includes(t.id)}
                        onChange={() => toggleTenant(t.id)}
                      />
                      {t.name}
                      {!t.slackChannelId ? (
                        <span className="text-muted-foreground text-xs">(no Slack channel)</span>
                      ) : null}
                    </label>
                  ))
                )}
              </div>
            </div>
          ) : null}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.emailOnPublish}
              onChange={(e) => update("emailOnPublish", e.target.checked)}
            />
            Offer email notification for this window
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.publish}
              onChange={(e) => update("publish", e.target.checked)}
            />
            Publish now (show on banner and calendar)
          </label>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={saving}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={submit} disabled={saving}>
            {saving ? "Saving…" : "Save window"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotifyButton({
  window,
  channel,
  onSent,
}: {
  window: StaffMaintenanceWindow;
  channel: MaintenanceChannel;
  onSent: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const label = channel === "slack" ? "Send to Slack" : "Send email";
  const targetCount =
    window.audience === "all" ? "all tenants" : `${window.tenantTargets.length} tenant(s)`;

  const send = async () => {
    setSending(true);
    try {
      const res: ChannelSendResult = await maintenanceAdminApi.notify(window.id, channel);
      const parts = [`${res.sent} sent`];
      if (res.skipped) parts.push(`${res.skipped} skipped`);
      if (res.failed) parts.push(`${res.failed} failed`);
      if (res.failed > 0) {
        toast.error(`${label}: ${parts.join(", ")}.`);
      } else {
        toast.success(`${label}: ${parts.join(", ")}.`);
      }
      setOpen(false);
      onSent();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : `Couldn't ${label.toLowerCase()}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{label}?</DialogTitle>
          <DialogDescription>
            This sends a {channel} notification for &ldquo;{window.title}&rdquo; to {targetCount}.
            {channel === "slack"
              ? " Tenants without a mapped Slack channel are skipped."
              : " All active users of the targeted tenants receive an email."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={sending}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={send} disabled={sending}>
            {sending ? "Sending…" : label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function kindBadge(w: StaffMaintenanceWindow) {
  return (
    <Badge variant={w.kind === "emergency" ? "destructive" : "outline"}>
      {w.kind === "emergency" ? "Emergency" : "Scheduled"}
    </Badge>
  );
}

function WindowCard({
  window,
  tenants,
  onChanged,
}: {
  window: StaffMaintenanceWindow;
  tenants: AdminTenant[];
  onChanged: () => void;
}) {
  const published = Boolean(window.publishedAt);

  const patch = async (input: Parameters<typeof maintenanceAdminApi.update>[1], ok: string) => {
    try {
      await maintenanceAdminApi.update(window.id, input);
      toast.success(ok);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Update failed.");
    }
  };

  const remove = async () => {
    await maintenanceAdminApi.remove(window.id);
    toast.success("Window deleted.");
    onChanged();
  };

  const range =
    new Date(window.startsAt).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }) +
    (window.endsAt
      ? ` - ${new Date(window.endsAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`
      : " (end TBD)");

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{window.title}</span>
          {kindBadge(window)}
          <Badge variant={published ? "secondary" : "outline"}>
            {published ? "Published" : "Draft"}
          </Badge>
        </div>
        <p className="text-muted-foreground text-sm">{range}</p>
        {window.description ? <p className="text-sm">{window.description}</p> : null}
        <p className="text-muted-foreground text-xs">
          Audience:{" "}
          {window.audience === "all"
            ? "All tenants"
            : window.tenantTargets.map((t) => t.name).join(", ") || "No tenants selected"}
        </p>

        {window.deliveries.length > 0 ? (
          <div className="text-muted-foreground flex flex-wrap gap-1.5 text-xs">
            {window.deliveries.slice(0, 6).map((d, i) => (
              <span key={i} className="bg-muted rounded px-1.5 py-0.5">
                {d.channel}: {d.status}
                {d.target ? ` (${d.target})` : ""}
              </span>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <select
            className={`${selectClass} h-8 w-auto`}
            value={window.status}
            onChange={(e) =>
              patch({ status: e.target.value as MaintenanceStatus }, "Status updated.")
            }
            aria-label="Status"
          >
            {(Object.keys(STATUS_LABELS) as MaintenanceStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>

          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              patch({ publish: !published }, published ? "Unpublished." : "Published.")
            }
          >
            {published ? "Unpublish" : "Publish"}
          </Button>

          <WindowFormDialog
            trigger={
              <Button variant="outline" size="sm">
                Edit
              </Button>
            }
            title="Edit maintenance window"
            tenants={tenants}
            initial={formFromWindow(window)}
            onSubmit={(input) => patch(input, "Window updated.")}
          />

          <NotifyButton window={window} channel="slack" onSent={onChanged} />
          {window.emailOnPublish ? (
            <NotifyButton window={window} channel="email" onSent={onChanged} />
          ) : null}

          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="sm" className="text-destructive">
                Delete
              </Button>
            }
            title="Delete maintenance window?"
            description={
              <>
                &ldquo;{window.title}&rdquo; will be removed from the banner and calendar. This
                can't be undone.
              </>
            }
            confirmLabel="Delete"
            pendingLabel="Deleting…"
            errorMessage="Delete failed."
            onConfirm={remove}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function StaffMaintenancePage() {
  const [windows, setWindows] = useState<StaffMaintenanceWindow[] | null>(null);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [slackConfigured, setSlackConfigured] = useState(true);
  const [error, setError] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [w, t] = await Promise.all([maintenanceAdminApi.list(), tenantsAdminApi.list()]);
      setWindows(w.windows);
      setSlackConfigured(w.slackConfigured);
      setTenants(t.tenants);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load maintenance windows.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = async (input: CreateWindowInput) => {
    await maintenanceAdminApi.create(input);
    toast.success("Maintenance window created.");
    await refresh();
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Maintenance"
        description="Schedule maintenance and notify affected tenants."
      >
        <WindowFormDialog
          trigger={
            <Button>
              <Plus className="size-4" />
              New window
            </Button>
          }
          title="New maintenance window"
          tenants={tenants}
          initial={emptyForm()}
          onSubmit={create}
        />
      </PageHeader>

      {!slackConfigured ? (
        <div className="border-border bg-muted/40 text-muted-foreground rounded-md border px-4 py-3 text-sm">
          Slack notifications are not configured yet (no bot token). Banner, calendar, and email
          still work; Slack sends will be skipped until the token is provisioned.
        </div>
      ) : null}

      {error && windows === null ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            Couldn't load maintenance windows. Refresh to try again.
          </CardContent>
        </Card>
      ) : windows === null ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : windows.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No maintenance windows</CardTitle>
            <CardDescription>
              Create one to notify tenants of planned or emergency work.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {windows.map((w) => (
            <WindowCard key={w.id} window={w} tenants={tenants} onChanged={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}
